import * as React from "react";
import { FileText, Image, Music, Video, X } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../primitives/button";

type AttachmentData = {
  id?: string;
  filename?: string;
  mediaType?: string;
  title?: string;
  type?: string;
  url?: string;
};

const getMediaCategory = (data?: AttachmentData): "image" | "video" | "audio" | "document" | "source" | "unknown" => {
  if (!data) return "unknown";
  if (data.type === "source-document") return "source";
  if (data.mediaType?.startsWith("image/")) return "image";
  if (data.mediaType?.startsWith("video/")) return "video";
  if (data.mediaType?.startsWith("audio/")) return "audio";
  if (data.mediaType) return "document";
  return "unknown";
};

const getAttachmentLabel = (data?: AttachmentData): string => {
  if (!data) return "Załącznik";
  return data.filename ?? data.title ?? "Załącznik";
};

type AttachmentsProps = React.HTMLAttributes<HTMLDivElement> & {
  variant?: "compact" | "grid" | "inline" | "list";
};

const AttachmentsContext = React.createContext<{
  data?: AttachmentData;
  onRemove?: () => void;
  variant: AttachmentsProps["variant"];
} | null>(null);

const useAttachment = () => {
  const context = React.useContext(AttachmentsContext);
  if (!context) throw new Error("Attachment components must be used within Attachment");
  return context;
};

const Attachments = React.forwardRef<HTMLDivElement, AttachmentsProps>(
  ({ className, variant = "grid", ...props }, ref) => (
    <AttachmentsContext.Provider value={{ variant }}>
      <div
        ref={ref}
        className={cn(
          variant === "grid" && "grid grid-cols-2 gap-2 sm:grid-cols-3",
          variant === "inline" &&
            "flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          variant === "compact" &&
            "flex max-w-full gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          variant === "list" && "flex flex-col gap-2",
          className,
        )}
        {...props}
      />
    </AttachmentsContext.Provider>
  ),
);
Attachments.displayName = "Attachments";

type AttachmentProps = React.HTMLAttributes<HTMLDivElement> & {
  data?: AttachmentData;
  onRemove?: () => void;
};

const Attachment = React.forwardRef<HTMLDivElement, AttachmentProps>(({ className, data, onRemove, ...props }, ref) => {
  const variant = React.useContext(AttachmentsContext)?.variant ?? "grid";

  return (
    <AttachmentsContext.Provider value={{ data, onRemove, variant }}>
      <div
        ref={ref}
        className={cn(
          "group relative overflow-hidden border border-gray-200 bg-white text-gray-700 shadow-sm",
          variant === "inline" && "flex max-w-56 shrink-0 items-center gap-2 rounded-md px-2 py-1.5 text-xs",
          variant === "compact" && "h-20 w-20 shrink-0 rounded-2xl p-0",
          variant === "list" && "flex items-center gap-3 rounded-md px-3 py-2 text-sm",
          variant === "grid" && "rounded-lg p-2 text-sm",
          className,
        )}
        {...props}
      />
    </AttachmentsContext.Provider>
  );
});
Attachment.displayName = "Attachment";

const AttachmentPreview = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { fallbackIcon?: React.ReactNode }
>(({ className, fallbackIcon, ...props }, ref) => {
  const { data, variant } = useAttachment();
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const category = getMediaCategory(data);
  const isImage = category === "image" && Boolean(data?.url);
  const label = getAttachmentLabel(data);
  const iconClassName = variant === "inline" ? "h-4 w-4" : variant === "compact" ? "h-6 w-6" : "h-5 w-5";
  const fallback =
    fallbackIcon ??
    (category === "image" ? (
      <Image className={iconClassName} />
    ) : category === "video" ? (
      <Video className={iconClassName} />
    ) : category === "audio" ? (
      <Music className={iconClassName} />
    ) : (
      <FileText className={iconClassName} />
    ));
  const imagePreview = isImage ? (
    <div
      aria-modal="true"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-gray-950/80 p-6"
      onClick={() => setPreviewOpen(false)}
      role="dialog"
    >
      <button
        type="button"
        aria-label="Zamknij podgląd"
        className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-gray-950 shadow-lg hover:bg-white"
        onClick={() => setPreviewOpen(false)}
      >
        <X className="h-5 w-5" />
      </button>
      <div className="max-h-full max-w-5xl" onClick={(event) => event.stopPropagation()}>
        <img
          src={data?.url}
          alt={label}
          className="max-h-[82vh] max-w-full rounded-lg bg-white object-contain shadow-2xl"
        />
        <div className="mt-3 text-center text-sm font-medium text-white">{label}</div>
      </div>
    </div>
  ) : null;
  const previewButtonClassName =
    "block h-full w-full cursor-zoom-in overflow-hidden border-0 bg-transparent p-0 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-950";

  if (variant === "compact") {
    return (
      <>
        <div
          ref={ref}
          className={cn(
            "flex h-full w-full items-center justify-center overflow-hidden rounded-2xl bg-gray-100 text-gray-500",
            className,
          )}
          {...props}
        >
          {isImage ? (
            <button
              type="button"
              className={cn(previewButtonClassName, "rounded-2xl")}
              onClick={() => setPreviewOpen(true)}
            >
              <img src={data?.url} alt={label} className="h-full w-full object-cover" />
            </button>
          ) : (
            fallback
          )}
        </div>
        {previewOpen ? imagePreview : null}
      </>
    );
  }

  if (isImage) {
    return (
      <>
        <div
          ref={ref}
          className={cn(
            "overflow-hidden rounded-md bg-gray-100",
            variant === "inline" && "h-6 w-6 shrink-0",
            variant !== "inline" && "mb-2 aspect-video w-full",
            className,
          )}
          {...props}
        >
          <button type="button" className={previewButtonClassName} onClick={() => setPreviewOpen(true)}>
            <img src={data?.url} alt={label} className="h-full w-full object-cover" />
          </button>
        </div>
        {previewOpen ? imagePreview : null}
      </>
    );
  }

  return (
    <div
      ref={ref}
      className={cn(
        "flex shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-500",
        variant === "inline" && "h-6 w-6",
        variant !== "inline" && "mb-2 h-10 w-10",
        className,
      )}
      {...props}
    >
      {fallback}
    </div>
  );
});
AttachmentPreview.displayName = "AttachmentPreview";

const AttachmentInfo = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { showMediaType?: boolean }
>(({ className, showMediaType, ...props }, ref) => {
  const { data, variant } = useAttachment();

  return (
    <div
      ref={ref}
      className={cn(
        "min-w-0 flex-1",
        variant === "compact" &&
          "pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-2 pb-1 pt-5 text-white",
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "truncate font-medium text-gray-800",
          variant === "inline" ? "text-xs" : "text-sm",
          variant === "compact" && "text-[10px] leading-3 text-white",
        )}
      >
        {getAttachmentLabel(data)}
      </div>
      {showMediaType && data?.mediaType ? (
        <div className={cn("truncate text-xs text-gray-500", variant === "compact" && "text-white/80")}>
          {data.mediaType}
        </div>
      ) : null}
    </div>
  );
});
AttachmentInfo.displayName = "AttachmentInfo";

const AttachmentRemove = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button> & { label?: string }>(
  ({ className, label = "Usuń", ...props }, ref) => {
    const { onRemove, variant } = useAttachment();

    if (!onRemove) return null;

    return (
      <Button
        ref={ref}
        type="button"
        variant="ghost"
        size="icon"
        aria-label={label}
        title={label}
        className={cn(
          "h-7 w-7 shrink-0 text-gray-500 hover:text-gray-950",
          variant === "compact" &&
            "absolute right-1 top-1 h-6 w-6 rounded-full bg-gray-950/85 text-white shadow-sm hover:bg-gray-950 hover:text-white",
          className,
        )}
        onClick={onRemove}
        {...props}
      >
        <X className="h-4 w-4" />
      </Button>
    );
  },
);
AttachmentRemove.displayName = "AttachmentRemove";

const AttachmentEmpty = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("rounded-md border border-dashed border-gray-200 p-3 text-sm text-gray-500", className)}
      {...props}
    />
  ),
);
AttachmentEmpty.displayName = "AttachmentEmpty";

export {
  Attachment,
  AttachmentEmpty,
  AttachmentInfo,
  AttachmentPreview,
  AttachmentRemove,
  Attachments,
  getAttachmentLabel,
  getMediaCategory,
  type AttachmentData,
};
